// @lib/canvas-utils.ts

import { decodeImageUrlInWorker } from '#lib/image-decode-client';
import { runWithWorkerFallback } from '#lib/worker-rpc';
import type { LazyWorkerRef } from '#types/types';

export function hexToRgb(hex: string): [number, number, number] | null {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  // The `replace` function's callback arguments (r, g, b) are guaranteed to be strings if a match occurs.
  hex = hex.replace(shorthandRegex, (m, r: string, g: string, b: string) => {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return null;
  }

  // Safely access the captured groups.
  const r = result[1];
  const g = result[2];
  const b = result[3];

  // Ensure all captured groups are defined before parsing.
  if (r === undefined || g === undefined || b === undefined) {
    return null;
  }

  return [
    parseInt(r, 16),
    parseInt(g, 16),
    parseInt(b, 16)
  ];
}

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(buffer).toString('base64');
  }
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.subarray(offset, offset + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  if (typeof btoa === 'function') {
    return btoa(binary);
  }
  throw new Error('Unable to convert ArrayBuffer to base64: no Buffer or btoa available.');
};

export const imageDataToDataURL = async (
  imageData: ImageData,
  format: string = 'image/png',
  quality?: number,
): Promise<string> => {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(imageData.width, imageData.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to acquire 2D context to serialize ImageData.');
    }
    ctx.putImageData(imageData, 0, 0);
    const blob = await canvas.convertToBlob({ type: format, quality });
    if (!blob) {
      throw new Error('OffscreenCanvas failed to convert ImageData to Blob.');
    }
    const buffer = await blob.arrayBuffer();
    const base64 = arrayBufferToBase64(buffer);
    const mime = blob.type && blob.type.trim().length > 0 ? blob.type : format;
    return `data:${mime};base64,${base64}`;
  }

  if (typeof document === 'undefined') {
    throw new Error('imageDataToDataURL requires a DOM or OffscreenCanvas environment.');
  }

  const canvas = document.createElement('canvas');
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to acquire 2D context to serialize ImageData.');
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL(format, quality);
};

const getCanvasForImageExtraction = (width: number, height: number): HTMLCanvasElement | OffscreenCanvas => {
  if (typeof OffscreenCanvas !== 'undefined') {
    return new OffscreenCanvas(width, height);
  }

  if (typeof document !== 'undefined') {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }

  throw new Error('loadImageDataFromUrl requires a DOM or OffscreenCanvas context.');
};

type Canvas2DContext = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

const isCanvas2DContext = (ctx: RenderingContext | OffscreenCanvasRenderingContext2D | null): ctx is Canvas2DContext => {
  if (!ctx) {
    return false;
  }
  return typeof (ctx as Canvas2DContext).clearRect === 'function'
    && typeof (ctx as Canvas2DContext).drawImage === 'function'
    && typeof (ctx as Canvas2DContext).getImageData === 'function';
};

// OpenCV keeps several full-size RGBA, Lab and float matrices during harmonization.
export const MAX_COLOR_HARMONIZATION_ANALYSIS_PIXELS = 2_000_000;

export const getColorHarmonizationAnalysisDimensions = (
  width: number,
  height: number,
  maxPixels: number = MAX_COLOR_HARMONIZATION_ANALYSIS_PIXELS,
): { width: number; height: number } => {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0 || maxPixels <= 0) {
    return { width: 0, height: 0 };
  }

  const sourceWidth = Math.max(1, Math.floor(width));
  const sourceHeight = Math.max(1, Math.floor(height));
  if (sourceWidth * sourceHeight <= maxPixels) {
    return { width: sourceWidth, height: sourceHeight };
  }

  const scale = Math.sqrt(maxPixels / (sourceWidth * sourceHeight));
  return {
    width: Math.max(1, Math.floor(sourceWidth * scale)),
    height: Math.max(1, Math.floor(sourceHeight * scale)),
  };
};

export const resizeImageDataToPixelBudget = (
  imageData: ImageData,
  maxPixels: number = MAX_COLOR_HARMONIZATION_ANALYSIS_PIXELS,
): ImageData => {
  const dimensions = getColorHarmonizationAnalysisDimensions(imageData.width, imageData.height, maxPixels);
  if (dimensions.width === 0 || dimensions.height === 0) {
    throw new Error('Cannot resize invalid ImageData dimensions.');
  }
  if (dimensions.width === imageData.width && dimensions.height === imageData.height) {
    return imageData;
  }

  const sourceCanvas = getCanvasForImageExtraction(imageData.width, imageData.height);
  const analysisCanvas = getCanvasForImageExtraction(dimensions.width, dimensions.height);
  try {
    const sourceContext = sourceCanvas.getContext('2d');
    const analysisContext = analysisCanvas.getContext('2d');
    if (!isCanvas2DContext(sourceContext) || !isCanvas2DContext(analysisContext)) {
      throw new Error('Failed to acquire a 2D context for color harmonization analysis.');
    }
    sourceContext.putImageData(imageData, 0, 0);
    analysisContext.drawImage(sourceCanvas, 0, 0, imageData.width, imageData.height, 0, 0, dimensions.width, dimensions.height);
    return analysisContext.getImageData(0, 0, dimensions.width, dimensions.height);
  } finally {
    sourceCanvas.width = 0;
    sourceCanvas.height = 0;
    analysisCanvas.width = 0;
    analysisCanvas.height = 0;
  }
};

const loadImageDataFromUrlOnMainThread = (url: string): Promise<ImageData> => {
  if (typeof Image === 'undefined') {
    return Promise.reject(new Error('Image is not supported in the current environment.'));
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const cleanup = () => {
      img.onload = null;
      img.onerror = null;
    };

    img.onload = () => {
      try {
        const width = Math.max(img.naturalWidth || img.width, 1);
        const height = Math.max(img.naturalHeight || img.height, 1);
        const canvas = getCanvasForImageExtraction(width, height);
        const ctx = canvas.getContext('2d');
        if (!isCanvas2DContext(ctx)) {
          throw new Error('Failed to acquire 2D context for image extraction.');
        }
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const imageData = ctx.getImageData(0, 0, width, height);
        resolve(imageData);
      } catch (error) {
        reject(error instanceof Error ? error : new Error('Failed to extract image data from URL.'));
      } finally {
        cleanup();
      }
    };

    img.onerror = () => {
      cleanup();
      reject(new Error(`Failed to load image at ${url}`));
    };

    img.src = url;
  });
};

// The worker must never import this module: a worker that imports its own launcher makes Turbopack recurse.
const imageDecodeWorker: LazyWorkerRef = { current: null, created: false };

/** Created on first use: a worker dropped as unavailable is never recreated. */
const ensureImageDecodeWorker = () => {
  if (imageDecodeWorker.created) return;
  imageDecodeWorker.created = true;
  if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined' || typeof createImageBitmap !== 'function') return;
  try {
    imageDecodeWorker.current = new Worker(new URL('../workers/image-decode.worker.ts', import.meta.url), { type: 'module' });
  } catch (error) {
    console.warn('[ImageDecode] Worker could not be created, decoding on the main thread.', error);
  }
};

export const loadImageDataFromUrl = async (url: string): Promise<ImageData> => {
  ensureImageDecodeWorker();
  const { result } = await runWithWorkerFallback(
    imageDecodeWorker,
    (worker) => decodeImageUrlInWorker(worker, url),
    () => loadImageDataFromUrlOnMainThread(url),
    '[ImageDecode] Worker decode failed, falling back to the main thread.',
  );
  return result;
};

const extractImageDataFromSource = (
  source: CanvasImageSource,
  width: number,
  height: number,
): ImageData => {
  const canvas = getCanvasForImageExtraction(width, height);
  const ctx = canvas.getContext('2d');
  if (!isCanvas2DContext(ctx)) {
    throw new Error('Failed to acquire 2D context for image extraction.');
  }
  ctx.drawImage(source, 0, 0, width, height);
  return ctx.getImageData(0, 0, width, height);
};

export const loadImageDataFromBlob = async (blob: Blob): Promise<ImageData> => {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(blob);
    try {
      return extractImageDataFromSource(bitmap, Math.max(bitmap.width, 1), Math.max(bitmap.height, 1));
    } finally {
      bitmap.close();
    }
  }

  if (typeof Image === 'undefined') {
    throw new Error('Image is not supported in the current environment.');
  }

  return new Promise<ImageData>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();

    img.onload = () => {
      try {
        const width = Math.max(img.naturalWidth || img.width, 1);
        const height = Math.max(img.naturalHeight || img.height, 1);
        resolve(extractImageDataFromSource(img, width, height));
      } catch (error) {
        reject(error instanceof Error ? error : new Error('Failed to extract image data from blob.'));
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image from blob.'));
    };

    img.src = objectUrl;
  });
};

interface Dimensions {
  width: number;
  height: number;
}

interface FindBestDimensionsOptions {
  /** The starting width, used to determine the target aspect ratio. Must be > 0. */
  startWidth: number;
  /** The starting height, used to determine the target aspect ratio. Must be > 0. */
  startHeight: number;
  /** The maximum allowed total pixels (width * height). Must be > 0. */
  targetPixels: number;
  /** The required multiple for both width and height. Must be an integer > 0. */
  multipleOf: number;
  /** Optional: Small tolerance for comparing floating-point ratios. Defaults to 1e-5. */
  ratioTolerance?: number;
}

/**
 * Computes the best output width/height combination respecting constraints.
 *
 * Prioritizes:
 * 1. Width and Height MUST be multiples of `multipleOf`.
 * 2. Total pixels (width * height) MUST NOT exceed `targetPixels`.
 * 3. Aspect ratio (width / height) should be as close as possible to `startWidth / startHeight`.
 * 4. Among pairs with similar best ratios, choose the one with the most pixels (closest to `targetPixels` from below).
 *
 * @param options - The input constraints.
 * @returns The best Dimensions object { width, height } found, or { width: 0, height: 0 } if no valid dimensions could be determined.
 */
export function findBestDimensions(options: FindBestDimensionsOptions): Dimensions {
  const {
    startWidth,
    startHeight,
    targetPixels,
    multipleOf,
    ratioTolerance = 1e-5, // Tolerance for ratio comparisons
  } = options;

  // --- Input Validation ---
  if (startWidth <= 0 || startHeight <= 0 || targetPixels <= 0 || multipleOf <= 0) {
    console.error("findBestDimensions: All input numbers must be positive.");
    return { width: 0, height: 0 };
  }
  if (multipleOf !== Math.floor(multipleOf)) {
     console.error("findBestDimensions: multipleOf must be an integer.");
     return { width: 0, height: 0 };
  }
   // Check if even the smallest possible dimension pair is too large
   if (multipleOf * multipleOf > targetPixels) {
     console.warn(`findBestDimensions: Smallest possible dimensions (${multipleOf}x${multipleOf}) exceed targetPixels (${targetPixels}).`);
     return { width: 0, height: 0 };
   }

  // --- Core Calculation ---
  const targetRatio = startWidth / startHeight;

  let bestPair: Dimensions = { width: 0, height: 0 };
  let minRatioDiff = Infinity;
  let maxPixels = 0; // Track max pixels *for the best ratio found so far*

  // Determine a practical maximum width to iterate.
  // If height is at least `multipleOf`, then w * multipleOf <= targetPixels.
  const maxWidth = Math.floor(targetPixels / multipleOf);

  for (let w = multipleOf; w <= maxWidth; w += multipleOf) {
    // Calculate the ideal height for this width based on the target ratio
    const idealHeight = w / targetRatio;

    // Find the two candidate heights (multiples of `multipleOf`) surrounding the ideal height
    // Ensure height is at least `multipleOf`
    const h1 = Math.max(multipleOf, Math.floor(idealHeight / multipleOf) * multipleOf);
    const h2 = Math.max(multipleOf, Math.ceil(idealHeight / multipleOf) * multipleOf);

    // Evaluate the two candidate pairs (w, h1) and (w, h2)
    // Using Set to avoid duplicate check if h1 === h2
    const candidateHeights = new Set([h1, h2]);

    for (const h of candidateHeights) {
       if (h === 0) continue; // Should be covered by Math.max(multipleOf, ...), but belts and suspenders

       const currentPixels = w * h;

       // Constraint 2: Check pixel limit
       if (currentPixels > targetPixels) {
         continue;
       }

       // Evaluate remaining constraints for this valid pair (w, h)
       const currentRatio = w / h;
       const currentRatioDiff = Math.abs(currentRatio - targetRatio);

       // --- Selection Logic ---
       // Is this ratio significantly better than the best found so far?
       if (currentRatioDiff < minRatioDiff - ratioTolerance) {
         // New best ratio found
         minRatioDiff = currentRatioDiff;
         maxPixels = currentPixels;
         bestPair = { width: w, height: h };
       }
       // Is this ratio almost the same as the best, but with more pixels?
       else if (
         Math.abs(currentRatioDiff - minRatioDiff) < ratioTolerance &&
         currentPixels > maxPixels
       ) {
         // Same best ratio tier, but closer to targetPixels
         maxPixels = currentPixels;
         bestPair = { width: w, height: h };
       }
    }
  }

  // If no pair was ever found (e.g., initial multipleOf*multipleOf check passed but no candidates worked)
  // return the initial {0, 0}. This might indicate impossible constraints.
  if (bestPair.width === 0 && bestPair.height === 0) {
      console.warn(`findBestDimensions: Could not find any valid dimension pair satisfying all constraints.`);
      // Consider if returning the smallest valid pair {multipleOf, multipleOf} is better if it fits targetPixels
      if (multipleOf * multipleOf <= targetPixels) {
          console.warn(`findBestDimensions: Returning smallest possible valid pair {${multipleOf}, ${multipleOf}} as fallback.`);
          return { width: multipleOf, height: multipleOf };
      }
  }


  return bestPair;
}








/**
 * Extracts frames from a video source at a given interval, ensuring the last frame is always included.
 *
 * @param videoSource - The URL or Blob of the video.
 * @param intervalSeconds - The interval in seconds at which to capture frames.
 * @returns A Promise that resolves with an object containing the array of frames, and the video's width and height.
 */
export const extractFramesFromVideo = async (
  videoSource: string | Blob,
  intervalSeconds: number = 0.8
): Promise<{ frames: { frameBlob: Blob; timestamp: number }[]; width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    const sourceUrl = videoSource instanceof Blob ? URL.createObjectURL(videoSource) : videoSource;
    const capturedFrames: { frameBlob: Blob; timestamp: number }[] = [];
    let currentTime = 0;
    let hasCapturedFinalFrame = false;

    const cleanup = () => {
      if (videoSource instanceof Blob) {
        URL.revokeObjectURL(sourceUrl);
      }
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
    };

    const onLoadedMetadata = () => {
      // Start the process by seeking to the first frame
      if (video.duration > 0) {
        video.currentTime = 0;
      } else {
        // Fallback for streams without duration
        video.currentTime = 0.1;
      }
    };

    const captureFrame = () => {
      return new Promise<void>((resolveCapture) => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth / 2;
        canvas.height = video.videoHeight / 2;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          console.error('Could not get 2D context for frame extraction.');
          return resolveCapture();
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (blob) {
            // Avoid adding duplicate frames if seek time is the same
            if (!capturedFrames.some(f => f.timestamp === video.currentTime)) {
              capturedFrames.push({ frameBlob: blob, timestamp: video.currentTime });
            }
          }
          resolveCapture();
        }, 'image/webp', 0.9);
      });
    };

    const onSeeked = async () => {
      await captureFrame();

      // If we just captured the final frame, we're done.
      if (hasCapturedFinalFrame) {
        cleanup();
        resolve({
          frames: capturedFrames,
          width: video.videoWidth,
          height: video.videoHeight,
        });
        return;
      }

      currentTime += intervalSeconds;

      if (currentTime < video.duration) {
        video.currentTime = currentTime;
      } else {
        // The interval-based loop is over. Now, capture the very last frame.
        hasCapturedFinalFrame = true;
        video.currentTime = video.duration;
      }
    };

    const onError = (e: Event | string) => {
      cleanup();
      const errorMessage = typeof e === 'string' ? e : (e.target as HTMLVideoElement)?.error?.message || 'Unknown video error';
      reject(new Error(`Error loading video for frame extraction: ${errorMessage}`));
    };

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);

    video.src = sourceUrl;
    video.load();
  });
};


/**
 * Corrects an image file to a target aspect ratio by cropping the image from the center.
 * Any transparency in the source image will be rendered over a black background.
 *
 * @param file The image file to process.
 * @param targetAspectRatio The desired aspect ratio (width / height).
 * @returns A promise that resolves to a new, corrected File object, or the original File if no correction was needed.
 */
export const cropImageFileToAspectRatio = async (file: File, targetAspectRatio: number): Promise<File> => {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(new Error(`Failed to load image for cropping: ${err}`));
    };
    img.src = url;
  });

  const sourceWidth = image.naturalWidth;
  const sourceHeight = image.naturalHeight;
  const sourceAspectRatio = sourceWidth / sourceHeight;

  // If aspect ratio is already correct (within a small tolerance), no need to process.
  if (Math.abs(sourceAspectRatio - targetAspectRatio) < 0.01) {
    return file;
  }

  let cropX = 0;
  let cropY = 0;
  let cropWidth = sourceWidth;
  let cropHeight = sourceHeight;

  // Determine crop dimensions
  if (sourceAspectRatio > targetAspectRatio) {
    // Source is wider than target: crop the sides
    cropWidth = sourceHeight * targetAspectRatio;
    cropX = (sourceWidth - cropWidth) / 2;
  } else {
    // Source is taller than target: crop the top and bottom
    cropHeight = sourceWidth / targetAspectRatio;
    cropY = (sourceHeight - cropHeight) / 2;
  }

  const finalCanvas = document.createElement('canvas');
  finalCanvas.width = Math.round(cropWidth);
  finalCanvas.height = Math.round(cropHeight);
  const finalCtx = finalCanvas.getContext('2d');
  if (!finalCtx) throw new Error('Could not get canvas context for cropping.');

  // Fill with black to handle potential transparency in the source PNG
  finalCtx.fillStyle = 'black';
  finalCtx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

  // Draw the cropped section of the original image onto the new canvas
  finalCtx.drawImage(
    image,
    cropX,       // source X
    cropY,       // source Y
    cropWidth,   // source Width
    cropHeight,  // source Height
    0,           // destination X
    0,           // destination Y
    finalCanvas.width,  // destination Width
    finalCanvas.height  // destination Height
  );

  const blob = await new Promise<Blob|null>(resolve => finalCanvas.toBlob(resolve, 'image/webp'));
  if (!blob) throw new Error('Failed to create blob from cropped canvas.');
  return new File([blob], file.name, { type: 'image/webp' });
};



/**
 * Fetch a video's bytes exactly once and read its intrinsic dimensions from those bytes.
 *
 * Mirrors the image hydration path (`loadImageDataFromUrl`) but for video: the raw bytes
 * are downloaded a single time into a Blob that the layer retains (see `Layer.videoBlob`),
 * so every `<video>` plays from a local `blob:` URL — no redundant network loads, no range
 * refetches on seek, no `stalled`. Metadata is read from a throwaway objectURL over the
 * already-downloaded blob (no second network request), which is revoked immediately after.
 */
const VIDEO_FETCH_TIMEOUT_MS = 45_000;
const VIDEO_METADATA_TIMEOUT_MS = 8_000;

export const loadVideoBlobAndMetadata = async (
  url: string,
  signal?: AbortSignal,
): Promise<{ blob: Blob; width: number; height: number; duration: number }> => {
  // FAIL FAST on the download itself — this is the real "is the asset reachable" gate.
  // A hung/failed fetch throws so the hydrator surfaces a clear [Layers] error instead of
  // silently spinning forever.
  const controller = new AbortController();
  const onExternalAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onExternalAbort, { once: true });
  }
  const fetchTimeoutId = setTimeout(() => controller.abort(), VIDEO_FETCH_TIMEOUT_MS);
  let blob: Blob;
  try {
    // Request the full file via Range so the /api/assets route serves it through its 206
    // partial path — the exact branch <video> elements exercise and that is proven in prod.
    // A no-Range GET would take the equivalent 200 full-stream branch (same underlying
    // blobClient.download stream, only the status/Content-Range differ), but pinning the 206
    // path keeps us on the most-travelled code and avoids relying on the browser's default
    // Range behaviour. The timeout/abort spans BOTH the fetch and the body read, so a genuinely
    // hung download surfaces as a real error instead of a silent spinner.
    const response = await fetch(url, {
      signal: controller.signal,
      headers: { Range: 'bytes=0-' },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch video bytes for ${url} (status ${response.status})`);
    }
    blob = await response.blob();
  } finally {
    clearTimeout(fetchTimeoutId);
    signal?.removeEventListener('abort', onExternalAbort);
  }

  // Metadata is BEST-EFFORT: reading intrinsic dimensions must never discard a downloaded,
  // playable blob or hang hydration. Some MP4s (moov atom at end, browser quirks) fire neither
  // `loadedmetadata` nor `error` on a detached element — so we cap it with a timeout and fall
  // back to zeroed dimensions, which the caller resolves against a sane default. The bytes are
  // what matter; the real <video> element reports true dimensions once it plays.
  const objectUrl = URL.createObjectURL(blob);
  try {
    const metadata = await new Promise<{ width: number; height: number; duration: number }>(
      (resolve) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;

        let settled = false;
        const finish = (result: { width: number; height: number; duration: number }) => {
          if (settled) return;
          settled = true;
          clearTimeout(metadataTimeoutId);
          video.onloadedmetadata = null;
          video.onerror = null;
          video.removeAttribute('src');
          try { video.load(); } catch { /* best effort */ }
          resolve(result);
        };

        const metadataTimeoutId = setTimeout(
          () => finish({ width: 0, height: 0, duration: 0 }),
          VIDEO_METADATA_TIMEOUT_MS,
        );
        video.onloadedmetadata = () =>
          finish({ width: video.videoWidth, height: video.videoHeight, duration: video.duration });
        video.onerror = () => finish({ width: 0, height: 0, duration: 0 });

        video.src = objectUrl;
      }
    );
    return { blob, ...metadata };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};